import { Link, Typography } from '@mui/material';
import i18next from 'i18next';
import { memo, useCallback, useId, useState } from 'react';

import { getClient } from '../../../../lib/info';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  applyMultiStorePointOfSales,
  closeMultiStorePanel,
  MULTI_STORE_MAX_ADDED,
  selectedIncludedPointOfSaleIds,
  selectedMultiStoreOrigin,
} from '../../../../store/multi-store';
import {
  getInitialVisibleCount,
  toggleDraftPointOfSaleId,
} from '../../../../utils/multi-store/selection';
import { focusMultiStorePanelToggle } from './multi-store-a11y';
import { MultiStoreButton } from './multi-store-button';
import { MultiStoreOriginCard } from './multi-store-origin-card';
import { MultiStorePanelCard } from './multi-store-panel-card';
import {
  trackMultiStoreBackToOrigin,
  trackMultiStoreSearch,
} from './multi-store-tracking';
import { useDistanceReference } from './use-distance-reference';
import { useMultiStoreCandidates } from './use-multi-store';

// Scelta dei centri in cui cercare. La selezione è una bozza locale, partita dagli aggiunti
// attuali all'apertura: si applica solo col bottone, così il calendario non cambia modalità
// a ogni tocco. «Annulla» chiude senza cambiare nulla.
export const MultiStorePanelContent = memo(
  function MultiStorePanelContent(): JSX.Element {
    const { numListPointOfSales } = getClient();
    const dispatch = useAppDispatch();
    const { data } = useMultiStoreCandidates();
    const includedIds = useAppSelector(selectedIncludedPointOfSaleIds);
    const origin = useAppSelector(selectedMultiStoreOrigin);
    const subtitle = useDistanceReference('sorted');
    const titleId = useId();

    const candidates = data ?? [];
    const isUnified = includedIds.length > 1;
    const [draft, setDraft] = useState<string[]>(() => includedIds.slice(1));
    const [visibleCount, setVisibleCount] = useState<number>(() =>
      getInitialVisibleCount(
        candidates.map((candidate) => candidate.id),
        includedIds.slice(1),
        numListPointOfSales
      )
    );
    const isFull = draft.length >= MULTI_STORE_MAX_ADDED;

    const handleToggle = useCallback((pointOfSaleId: string) => {
      setDraft((current) =>
        toggleDraftPointOfSaleId(current, pointOfSaleId, MULTI_STORE_MAX_ADDED)
      );
    }, []);

    const handleShowMore = useCallback(() => {
      setVisibleCount((count) => count + numListPointOfSales);
    }, [numListPointOfSales]);

    // Alla chiusura il pannello si smonta: il focus torna al link della riga sopra
    const handleCancel = useCallback(() => {
      dispatch(closeMultiStorePanel());
      focusMultiStorePanelToggle();
    }, [dispatch]);

    // Con la bozza vuota («Cerca solo in …») si torna al calendario classico dell'origin
    const handleApply = useCallback(() => {
      const isBackToOrigin = dispatch(applyMultiStorePointOfSales(draft));
      focusMultiStorePanelToggle();
      if (draft.length > 0) trackMultiStoreSearch(draft.length + 1);
      if (isBackToOrigin || draft.length === 0) trackMultiStoreBackToOrigin();
    }, [dispatch, draft]);

    return (
      <div className="multi-store-panel">
        <div className="multi-store-heading">
          <Typography variant="contentTitle2" id={titleId}>
            {i18next.t('pages.when.multi_store.panel_title')}
          </Typography>
          <Typography variant="contentDescription2">{subtitle}</Typography>
        </div>
        <div
          role="group"
          aria-labelledby={titleId}
          className="multi-store-cards"
        >
          <MultiStoreOriginCard pointOfSale={origin} />
          {candidates.slice(0, visibleCount).map((pointOfSale) => {
            const isSelected = draft.includes(pointOfSale.id);
            return (
              <MultiStorePanelCard
                key={pointOfSale.id}
                pointOfSale={pointOfSale}
                isSelected={isSelected}
                isDisabled={isFull && !isSelected}
                onToggle={handleToggle}
              />
            );
          })}
        </div>
        {isFull && (
          <Typography variant="contentDescription2" className="multi-store-note">
            {i18next.t('pages.when.multi_store.max_reached', {
              max: MULTI_STORE_MAX_ADDED + 1,
            })}
          </Typography>
        )}
        {visibleCount < candidates.length && (
          <div className="multi-store-actions">
            <MultiStoreButton onClick={handleShowMore}>
              {i18next.t('pages.where.see_other')}
            </MultiStoreButton>
          </div>
        )}
        <div className="multi-store-actions">
          <Link
            component="button"
            variant="contentDescription2"
            className="multi-store-link"
            onClick={handleCancel}
          >
            {i18next.t('pages.when.multi_store.cancel')}
          </Link>
          {(draft.length > 0 || isUnified) && (
            <MultiStoreButton onClick={handleApply}>
              {draft.length > 0
                ? i18next.t('pages.when.multi_store.search', {
                    count: draft.length + 1,
                  })
                : i18next.t('pages.when.multi_store.search_only', {
                    name: origin.name,
                  })}
            </MultiStoreButton>
          )}
        </div>
      </div>
    );
  }
);
